import './table.scss';

import { Table, type TableColumnProps, type TableProps } from 'antd';
import {
  Children,
  type ReactElement,
  type ReactNode,
  cloneElement
} from 'react';

import { LocalizedLink } from '../localized-link';
import MainLoader from '../main-loader';
import selectComponentClass from './parts/select-component-class';

interface IProps<T> extends Omit<TableProps<T>, 'onRow'> {
  onRowClick?: (row: T) => void;
  rowLink?: (row: T) => string | undefined;
}

const CustomTable = <T extends object>({
  onRowClick,
  rowLink,
  ...props
}: IProps<T>) => {
  // Функция для рекурсивного прохода по `children`
  const processChildren = (children: ReactNode): ReactNode => {
    return Children.map(children, (child) => {
      if (!child || typeof child !== 'object' || !('type' in child)) {
        return child; // Пропускаем не-React элементы
      }

      const childElement = child as ReactElement<
        unknown,
        string | React.ComponentType<unknown>
      >;

      // Проверяем, является ли элемент `Table.Column`
      const isColumn =
        childElement.type &&
        typeof childElement.type === 'object' &&
        'name' in childElement.type &&
        (childElement.type as { name: string }).name === 'Column';

      if (isColumn) {
        const columnProps = childElement.props as TableColumnProps<T>;
        const render = columnProps.render;

        return cloneElement(
          childElement as ReactElement<TableColumnProps<T>>,
          {
            render: (value: unknown, record: T, index: number) => {
              const href = rowLink?.(record);
              const originalRender: ReactNode = render
                ? (render(value, record, index) as ReactNode)
                : (value as ReactNode);

              return href ? (
                <LocalizedLink
                  to={href}
                  onClick={(e) => {
                    if (onRowClick) e.preventDefault();
                  }}
                  data-table-link="true"
                >
                  {originalRender}
                </LocalizedLink>
              ) : (
                originalRender
              );
            },
            children: processChildren(columnProps.children)
          } as Partial<TableColumnProps<T>>
        );
      }

      const props = childElement.props as { children?: ReactNode };
      return cloneElement(
        childElement as ReactElement<{ children?: ReactNode }>,
        {
          children: processChildren(props.children)
        } as Partial<{ children?: ReactNode }>
      );
    });
  };

  return (
    <Table<T>
      {...props}
      columns={props.columns?.map((item) => {
        return {
          ...item,
          render: (value, record, index) => {
            const href = rowLink?.(record);
            const render =
              item && item.render ? (
                item.render(value, record, index)
              ) : (
                <>{value}</>
              );

            return href ? (
              <LocalizedLink
                to={href}
                onClick={(e) => {
                  if (onRowClick) e.preventDefault();
                }}
                data-table-link="true"
              >
                {render as ReactNode}
              </LocalizedLink>
            ) : (
              render
            );
          }
        };
      })}
      size="small"
      showSorterTooltip={false}
      className={'table ' + props.className}
      rowClassName={`table__row ${onRowClick ? 'table__row--clickable' : ''}`}
      pagination={{
        placement: ['bottomStart'],
        showSizeChanger: true,
        ...props.pagination,
        pageSizeOptions: [10, 20, 50],
        // @ts-expect-error - типы не совпадают
        selectComponentClass: selectComponentClass(props.pagination)
      }}
      scroll={{
        x: 'max-content'
      }}
      onRow={(r) => {
        return {
          onClick: () => {
            onRowClick?.(r);
          }
        };
      }}
      loading={{
        spinning: !!props.loading,
        indicator: <MainLoader fullWidth />
      }}
    >
      {props.children && processChildren(props.children)}
    </Table>
  );
};

export default CustomTable;
